import { PaginationMetadata } from '@execonline-inc/resource';
import { assertNever } from '@kofno/piper';
import { just, Maybe, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import { error } from '../ErrorHandling';
import {
  getSearchParametersFromUrl,
  getUpdatedSearchParameters,
  getUpdatedSortParameters,
} from '../ListFilter';
import { SearchParameter, SearchParameters } from '../ListFilter/Types';
import ParticipantStore from '../ParticipantStore';
import { Link } from '../Resource/Types';
import { TPlainTextKey } from '../Translations';
import ParticipantsOptionsStore from './Options';
import {
  loading,
  loadingMore,
  ParticipantsResource,
  ready,
  Ready,
  SortableColumn,
  sorting,
  State,
  updatingOrganizationParameters,
  updatingQuery,
  updatingSearchParameters,
  initializing,
} from './Types';
import { StringOption } from '../components/Options/Types';

class ParticipantsStore {
  @observable
  state: State = initializing(getSearchParametersFromUrl());

  readonly optionsStore: ParticipantsOptionsStore;

  constructor() {
    makeObservable(this);
    this.optionsStore = new ParticipantsOptionsStore(this);
  }

  @action
  ready = (resource: ParticipantsResource, participantStores: ReadonlyArray<ParticipantStore>) => {
    switch (this.state.kind) {
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
        this.state = ready(this.state, resource, participantStores);
        break;
      case 'initializing':
      case 'ready':
      case 'sorting':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loading = (page: number) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'sorting':
      case 'ready':
      case 'updating-search-parameters':
        this.state = loading(page, this.state);
        break;
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  error = (msg: TPlainTextKey) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'error':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'loading':
      case 'loading-more':
      case 'ready':
        this.state = error(msg);
        break;
      default:
        assertNever(this.state);
    }
  };

  updatedSearchParameters = (searchParameter: SearchParameter, state: Ready) =>
    getUpdatedSearchParameters(
      {
        newSearchParameter: searchParameter,
        searchParameters: state.searchParameters,
      },
      'participants_filter',
    );

  @action
  updatingQuery = (searchParameter: SearchParameter) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
        break;
      case 'ready':
        const searchParameters = this.updatedSearchParameters(searchParameter, this.state);
        this.state = updatingQuery(searchParameters, this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updatingSearchParameters = (searchParameter: SearchParameter) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
        break;
      case 'ready':
        const searchParameters = this.updatedSearchParameters(searchParameter, this.state);
        this.state = updatingSearchParameters(searchParameters, this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updatingOrganizationParameters = (option: StringOption) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'updating-search-parameters':
      case 'sorting':
      case 'updating-query':
      case 'error':
        break;
      case 'ready':
        this.state = updatingOrganizationParameters(this.state, option);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  sorting = (sortedColumn: SortableColumn) => {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'error':
        break;
      case 'ready':
        const searchParameters = getUpdatedSortParameters(
          { searchParameters: this.state.searchParameters, sortedColumn },
          'participants_filter',
        );
        this.state = sorting(searchParameters, this.state);
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  loadMore = (page: number) => {
    switch (this.state.kind) {
      case 'ready':
        this.state = loadingMore(page, this.state);
        break;
      case 'initializing':
      case 'sorting':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'loading':
      case 'loading-more':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  selectAll = () => {
    this.participantStores.map((store) => store.updateParticipantChecked(true));
  };

  @action
  deselectAll = () => {
    this.participantStores.map((store) => store.updateParticipantChecked(false));
  };

  @action
  reloadList = () => {
    switch (this.state.kind) {
      case 'ready':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'loading-more':
        this.loading(this.state.page);
        break;
      case 'initializing':
      case 'loading':
      case 'error':
        this.loading(1);
        break;
      default:
        assertNever(this.state);
    }
  };

  @computed
  get participantStores(): ReadonlyArray<ParticipantStore> {
    switch (this.state.kind) {
      case 'ready':
      case 'updating-query':
      case 'loading-more':
      case 'updating-search-parameters':
      case 'sorting':
        return this.state.participantStores;
      case 'loading':
      case 'initializing':
      case 'error':
        return [];
    }
  }

  @computed
  get links(): ReadonlyArray<Link> {
    switch (this.state.kind) {
      case 'ready':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
      case 'loading-more':
        return this.state.resource.links;
      case 'loading':
      case 'initializing':
      case 'error':
        return [];
    }
  }

  @computed
  get searchParameters(): SearchParameters {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'ready':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
        return this.state.searchParameters;
      case 'error':
        return [];
    }
  }

  @computed
  get selectedOrganization(): Maybe<StringOption> {
    switch (this.state.kind) {
      case 'initializing':
      case 'loading':
      case 'loading-more':
      case 'ready':
      case 'updating-query':
      case 'sorting':
      case 'updating-search-parameters':
        return this.state.selectedOrganization;
      case 'error':
        return nothing();
    }
  }

  @computed
  get metadata(): Maybe<PaginationMetadata> {
    switch (this.state.kind) {
      case 'ready':
      case 'loading-more':
      case 'updating-query':
      case 'updating-search-parameters':
      case 'sorting':
        return just(this.state.resource.metadata);
      case 'initializing':
      case 'loading':
      case 'error':
        return nothing();
    }
  }

  @computed
  get checkedParticipants(): ReadonlyArray<ParticipantStore> {
    switch (this.state.kind) {
      case 'ready':
      case 'updating-search-parameters':
      case 'updating-query':
      case 'sorting':
      case 'loading-more':
        return this.state.participantStores.filter((s) => s.participantChecked == true);
      case 'initializing':
      case 'loading':
      case 'error':
        return [];
    }
  }
}

export default ParticipantsStore;
